import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import CodeViewModal from '../../../../../components/modals/CodeViewModal';

import {
  GetScNodeRevisionStatusBIZcoreTD
} from '../../../../../redux/actions/business/core/scNodeRevisionStatusActions';

const ScNodeRevisionStatusPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [statusList, setStatusList] = useState([]);
  const [statusTotal, setStatusTotal] = useState(0);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalTitle, setViewModalTitle] = useState("");
  const [viewModalContent, setViewModalContent] = useState("");

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  // This is the cross-reference — "in revision R, node N reads content C" —
  // so it carries everything that varies by position or by revision.
  const tableHeadingsList = [
    { label: "Node label path", sort: "node_label_path" },
    { label: "Revision", sort: "ref_revision_id" },
    { label: "Type", sort: null },
    { label: "Delta", sort: "delta_status" },
    { label: "Content hash", sort: "observed_text_hash" },
    { label: "Legal text", sort: null },
    { label: "Pages", sort: "page_from" },
    { label: "Effective from", sort: "effective_from" },
    { label: "Effective to", sort: "effective_to" },
    { label: "Source", sort: "segmentation_source" },
    { label: "", sort: null },
  ];

  const DELTA_COLORS = {
    NEW:             'success',
    UNCHANGED:       'info',
    CONTENT_CHANGED: 'warning',
    DELETED:         'error',
  };

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setStatusList(state.scNodeRevisionStatusList.records || []);
      setStatusTotal(state.scNodeRevisionStatusList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetStatus(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetScNodeRevisionStatusBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const truncate = (text, maxLen = 80) => {
    if (!text) return null;
    return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
  };

  const handleViewRawText = (row) => {
    setViewModalTitle(`${row.node_label_path || 'Node'} — ${row.revision_name || ''}`);
    setViewModalContent(row.raw_legal_text || '');
    setViewModalOpen(true);
  };

  return (
    <Box>

      <CodeViewModal
        open={viewModalOpen}
        setOpen={setViewModalOpen}
        title={viewModalTitle}
        language="text"
        content={viewModalContent}
      />

      <TopBar
        title={"SC Node Revision Status"}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          statusList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No SC node revision status found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                statusList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' fontWeight={600} fontFamily="monospace">
                        {row.node_label_path || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        {row.revision_name || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        {row.node_type || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.delta_status
                        ? <Chip
                            size="small"
                            label={row.delta_status}
                            color={DELTA_COLORS[row.delta_status] || 'default'}
                            variant={row.delta_status === 'UNCHANGED' ? 'outlined' : 'filled'}
                            sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                          />
                        : <NA />}
                    </TableCell>
                    <TableCell>
                      {/* The content this placement points at. Rows sharing a
                          hash share one stored row of text. */}
                      {row.observed_text_hash
                        ? <Tooltip title={row.observed_text_hash} placement="top">
                            <Typography variant='caption' color={"grey"} fontFamily="monospace">
                              {truncate(row.observed_text_hash, 12)}
                            </Typography>
                          </Tooltip>
                        : <NA />}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant='caption' color={"grey"} sx={{ wordBreak: 'break-word' }}>
                        {truncate(row.raw_legal_text, 90) || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        {row.page_from != null
                          ? `${row.page_from}${row.page_to && row.page_to !== row.page_from ? `-${row.page_to}` : ''}`
                          : <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>{row.effective_from || <NA />}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>{row.effective_to || <NA />}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>{row.segmentation_source || <NA />}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.raw_legal_text
                        ? <Tooltip title="View legal text">
                            <IconButton size="small" color="primary" onClick={() => handleViewRawText(row)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        : null}
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={11}
                  count={statusTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

    </Box>
  );
};

export default ScNodeRevisionStatusPage;
